import React, { useState, useCallback } from "react";
import { Dialog, DialogBody } from "../../../../design/dialog";
import { Button } from "../../../../design/button";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  WizardFormData,
  ExtractedIntakeData,
  UploadedFileInfo,
  createInitialFormData,
  extractedToFormData,
} from "./types";
import UploadStep from "./UploadStep";
import BasicsStep from "./BasicsStep";
import ParentsStep from "./ParentsStep";
import ChildrenStep from "./ChildrenStep";
import ReviewStep from "./ReviewStep";
import { useCase } from "../../../../contexts/CaseContext";
import { useParent } from "../../../../contexts/ParentContext";
import { useChild } from "../../../../contexts/ChildContext";
import { useNavigate } from "react-router-dom";
import {
  ReasonForSupervision,
  RelationshipToChild,
  CustodyStatus,
  Sex,
  Race,
  PrimaryLanguage,
  MaritalStatus,
  IncomeLevel,
  EducationLevel,
} from "@visitproof/types";
import { Timestamp } from "firebase/firestore";

interface ImportCaseWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
  { id: 0, name: "Upload", shortName: "Upload" },
  { id: 1, name: "Case Details", shortName: "Details" },
  { id: 2, name: "Parents", shortName: "Parents" },
  { id: 3, name: "Children", shortName: "Children" },
  { id: 4, name: "Review", shortName: "Review" },
];

const ImportCaseWizard: React.FC<ImportCaseWizardProps> = ({ isOpen, onClose }) => {
  const { createCase, addFileToCase } = useCase();
  const { createParent } = useParent();
  const { createChild } = useChild();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>(createInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extractionConfidence, setExtractionConfidence] = useState<number | null>(null);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<UploadedFileInfo | null>(null);

  const handleExtracted = useCallback((data: ExtractedIntakeData, fileInfo?: UploadedFileInfo) => {
    const converted = extractedToFormData(data);
    setFormData(converted);
    setExtractionConfidence(data.confidence || null);
    if (fileInfo) {
      setUploadedFileInfo(fileInfo);
    }
    setCurrentStep(1);
  }, []);

  const handleSkipUpload = useCallback(() => {
    setFormData(createInitialFormData());
    setExtractionConfidence(null);
    setUploadedFileInfo(null);
    setCurrentStep(1);
  }, []);

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleEditStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const resetWizard = useCallback(() => {
    setCurrentStep(0);
    setFormData(createInitialFormData());
    setExtractionConfidence(null);
    setUploadedFileInfo(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const caseId = await createCase({
        identifier: formData.case.identifier,
        notes: formData.case.notes || undefined,
        courtAddress: {
          county: formData.case.courtCounty,
          branchName: formData.case.courtBranchName,
          streetAddress: formData.case.courtStreetAddress,
          mailingAddress: "",
          city: formData.case.courtCity,
          zipCode: formData.case.courtZipCode,
        },
        reasonForSupervision: formData.case.reasonForSupervision
          ? (formData.case.reasonForSupervision as ReasonForSupervision)
          : undefined,
        referralSource: formData.case.referralSource || undefined,
      });

      for (const parent of formData.parents) {
        if (parent.firstName && parent.lastName) {
          await createParent({
            caseId,
            firstName: parent.firstName,
            lastName: parent.lastName,
            email: parent.email || "",
            phoneNumber: parent.phoneNumber || "",
            alternatePhone: parent.alternatePhone || undefined,
            dob: parent.dateOfBirth
              ? Timestamp.fromDate(new Date(parent.dateOfBirth))
              : undefined,
            relationshipToChild: parent.relationshipToChild
              ? (parent.relationshipToChild as RelationshipToChild)
              : RelationshipToChild.Other,
            custodyStatus: parent.custodyStatus
              ? (parent.custodyStatus as CustodyStatus)
              : CustodyStatus.NonCustodial,
            isVisitingParent: parent.isVisitingParent || false,
            isCustodialParent: parent.isCustodialParent || false,
            address: parent.streetAddress ? {
              streetAddress: parent.streetAddress,
              city: parent.city,
              state: parent.state,
              zipCode: parent.zipCode,
            } : undefined,
            // Emergency contact
            emergencyContact: parent.emergencyContactName ? {
              name: parent.emergencyContactName,
              relationship: parent.emergencyContactRelationship || "",
              phoneNumber: parent.emergencyContactPhone || undefined,
            } : undefined,
            // Demographics
            sex: (parent.sex as Sex) || undefined,
            race: (parent.race as Race) || undefined,
            primaryLanguage: (parent.primaryLanguage as PrimaryLanguage) || undefined,
            maritalStatus: (parent.maritalStatus as MaritalStatus) || undefined,
            incomeLevel: (parent.incomeLevel as IncomeLevel) || undefined,
            educationLevel: (parent.educationLevel as EducationLevel) || undefined,
            translatorNeeded: parent.translatorNeeded || false,
            isRuralArea: parent.isRuralArea || false,
            // Health information
            medicalProblems: parent.medicalProblems || undefined,
            allergies: parent.allergies || undefined,
            // Attorney (legacy field)
            attorneyName: parent.attorneyName || undefined,
          });
        }
      }

      for (const child of formData.children) {
        if (child.firstName && child.lastName) {
          await createChild({
            caseId,
            firstName: child.firstName,
            lastName: child.lastName,
            dob: child.dateOfBirth
              ? Timestamp.fromDate(new Date(child.dateOfBirth))
              : undefined,
            hasSpecialNeeds: child.hasSpecialNeeds,
            specialNeedsNotes: child.specialNeeds || undefined,
            allergies: child.allergies || undefined,
            notes: child.notes || undefined,
          });
        }
      }

      if (uploadedFileInfo) {
        await addFileToCase(caseId, {
          name: uploadedFileInfo.fileName,
          description: "Intake form (imported via AI extraction)",
          path: uploadedFileInfo.storagePath,
          caseId,
          isVisibleToSupervisor: false,
          type: "application/pdf",
        });
      }

      navigate(`/case/${caseId}`);
      resetWizard();
      onClose();
    } catch (error) {
      console.error("Error creating case:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, uploadedFileInfo, createCase, createParent, createChild, addFileToCase, navigate, resetWizard, onClose]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setCurrentStep(0);
      setFormData(createInitialFormData());
      setExtractionConfidence(null);
      setUploadedFileInfo(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <UploadStep
            onExtracted={handleExtracted}
            onSkip={handleSkipUpload}
          />
        );
      case 1:
        return (
          <BasicsStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onBack={handleBack}
            extractionConfidence={extractionConfidence}
          />
        );
      case 2:
        return (
          <ParentsStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <ChildrenStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <ReviewStep
            formData={formData}
            onBack={handleBack}
            onSubmit={handleSubmit}
            onEditStep={handleEditStep}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="4xl">
      {/* Header with close button */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg dark:bg-blue-900/30">
            <DocumentArrowUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
              Import Case from PDF
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].name}
            </p>
          </div>
        </div>
        <Button
          type="button"
          plain
          onClick={handleClose}
          disabled={isSubmitting}
          className="!p-2"
        >
          <XMarkIcon className="w-5 h-5" />
          <span className="sr-only">Close</span>
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <div
                  className={clsx(
                    "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors",
                    currentStep > step.id
                      ? "bg-blue-600 text-white"
                      : currentStep === step.id
                      ? "border-2 border-blue-600 bg-white text-blue-600 dark:bg-zinc-900"
                      : "border-2 border-zinc-300 bg-white text-zinc-400 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-500"
                  )}
                >
                  {currentStep > step.id ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    step.id + 1
                  )}
                </div>
                <span
                  className={clsx(
                    "mt-2 text-xs font-medium hidden sm:block",
                    currentStep >= step.id
                      ? "text-zinc-900 dark:text-zinc-100"
                      : "text-zinc-400 dark:text-zinc-500"
                  )}
                >
                  {step.shortName}
                </span>
              </div>

              {/* Connector line */}
              {index < STEPS.length - 1 && (
                <div
                  className={clsx(
                    "flex-1 h-0.5 mx-2 sm:mx-4 transition-colors",
                    currentStep > step.id
                      ? "bg-blue-600"
                      : "bg-zinc-200 dark:bg-zinc-700"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <DialogBody className="mt-0">
        {renderStep()}
      </DialogBody>
    </Dialog>
  );
};

export default ImportCaseWizard;
